import React, { useRef, useEffect, useState, forwardRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';

const LiquidGlassInput = forwardRef(({
  type = "text",
  placeholder = "Enter text...",
  value = "",
  onChange,
  onFocus,
  onBlur,
  variant = "primary",
  size = "medium",
  disabled = false,
  error = false,
  success = false,
  label = "",
  helperText = "",
  required = false,
  className = "",
  style = {},
  ...props
}, ref) => {
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(!!value);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Motion values for parallax effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Transform mouse position to subtle rotation and translation
  const rotateX = useTransform(mouseY, [-100, 100], [5, -5]);
  const rotateY = useTransform(mouseX, [-100, 100], [-5, 5]);
  const translateZ = useTransform(mouseX, [-100, 100], [-5, 5]);
  
  // Spring animations for smooth transitions
  const springConfig = { damping: 20, stiffness: 200 };
  const springRotateX = useSpring(rotateX, springConfig);
  const springRotateY = useSpring(rotateY, springConfig);
  const springTranslateZ = useSpring(translateZ, springConfig);

  // Handle mouse movement for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!inputRef.current || disabled) return;
      
      const rect = inputRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const x = e.clientX - centerX;
      const y = e.clientY - centerY;
      
      mouseX.set(x);
      mouseY.set(y);
      setMousePosition({ x, y });
    };

    const handleMouseLeave = () => {
      mouseX.set(0);
      mouseY.set(0);
      setMousePosition({ x: 0, y: 0 });
    };

    const input = inputRef.current;
    if (input) {
      input.addEventListener('mousemove', handleMouseMove);
      input.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (input) {
        input.removeEventListener('mousemove', handleMouseMove);
        input.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [mouseX, mouseY, disabled]);

  // Update filled state when value changes
  useEffect(() => {
    setIsFilled(!!value);
  }, [value]);

  // Variant styles
  const variants = {
    primary: {
      background: `
        linear-gradient(135deg, 
          rgba(255, 255, 255, 0.08) 0%, 
          rgba(255, 255, 255, 0.03) 50%, 
          rgba(255, 255, 255, 0.08) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(255, 255, 255, 0.1) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 255, 255, 0.3)',
      focusBorderColor: 'rgba(0, 123, 255, 0.6)',
      color: 'rgba(255, 255, 255, 0.9)',
      placeholderColor: 'rgba(255, 255, 255, 0.5)',
      shadowColor: 'rgba(0, 123, 255, 0.2)'
    },
    secondary: {
      background: `
        linear-gradient(135deg, 
          rgba(255, 255, 255, 0.06) 0%, 
          rgba(255, 255, 255, 0.02) 50%, 
          rgba(255, 255, 255, 0.06) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(255, 255, 255, 0.08) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      focusBorderColor: 'rgba(255, 255, 255, 0.5)',
      color: 'rgba(255, 255, 255, 0.8)',
      placeholderColor: 'rgba(255, 255, 255, 0.4)',
      shadowColor: 'rgba(255, 255, 255, 0.15)'
    },
    success: {
      background: `
        linear-gradient(135deg, 
          rgba(40, 167, 69, 0.08) 0%, 
          rgba(40, 167, 69, 0.03) 50%, 
          rgba(40, 167, 69, 0.08) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(40, 167, 69, 0.1) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(40, 167, 69, 0.3)',
      focusBorderColor: 'rgba(40, 167, 69, 0.6)',
      color: 'rgba(255, 255, 255, 0.9)',
      placeholderColor: 'rgba(255, 255, 255, 0.5)',
      shadowColor: 'rgba(40, 167, 69, 0.2)'
    },
    danger: {
      background: `
        linear-gradient(135deg, 
          rgba(220, 53, 69, 0.08) 0%, 
          rgba(220, 53, 69, 0.03) 50%, 
          rgba(220, 53, 69, 0.08) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(220, 53, 69, 0.1) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(220, 53, 69, 0.3)',
      focusBorderColor: 'rgba(220, 53, 69, 0.6)',
      color: 'rgba(255, 255, 255, 0.9)',
      placeholderColor: 'rgba(255, 255, 255, 0.5)',
      shadowColor: 'rgba(220, 53, 69, 0.2)'
    }
  };

  // Size styles
  const sizes = {
    small: {
      padding: '8px 12px',
      fontSize: '14px',
      borderRadius: '8px',
      minHeight: '36px',
      labelFontSize: '12px'
    },
    medium: {
      padding: '12px 16px',
      fontSize: '16px',
      borderRadius: '12px',
      minHeight: '48px',
      labelFontSize: '14px'
    },
    large: {
      padding: '16px 20px',
      fontSize: '18px',
      borderRadius: '16px',
      minHeight: '56px',
      labelFontSize: '16px'
    }
  };

  const currentVariant = variants[variant] || variants.primary;
  const currentSize = sizes[size] || sizes.medium;

  // Determine border color based on state
  const getBorderColor = () => {
    if (error) return 'rgba(220, 53, 69, 0.6)';
    if (success) return 'rgba(40, 167, 69, 0.6)';
    if (isFocused) return currentVariant.focusBorderColor;
    return currentVariant.borderColor;
  };

  // Determine shadow color based on state
  const getShadowColor = () => {
    if (error) return 'rgba(220, 53, 69, 0.3)';
    if (success) return 'rgba(40, 167, 69, 0.3)';
    return currentVariant.shadowColor;
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e) => {
    setIsFilled(!!e.target.value);
    onChange?.(e);
  };

  // Custom increment/decrement for number input
  const isNumber = type === 'number';
  const isFullname = type === 'fullname';
  const handleStep = (step) => {
    if (disabled) return;
    let newValue = value === '' || value === null ? 0 : Number(value);
    newValue += step;
    if (onChange) {
      // Create a synthetic event to match React's onChange signature
      const event = { target: { value: String(newValue) } };
      onChange(event);
    }
  };

  // For fullname, value should be { firstname, lastname }
  const handleFullnameChange = (field, fieldValue) => {
    if (onChange) {
      const newValue = { ...value, [field]: fieldValue };
      // Create a synthetic event to match React's onChange signature
      const event = { target: { value: newValue } };
      onChange(event);
    }
  };

  return (
    <motion.div
      ref={inputRef}
      className={`liquid-glass-input-container ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        ...style
      }}
      animate={{
        rotateX: disabled ? 0 : springRotateX,
        rotateY: disabled ? 0 : springRotateY,
        translateZ: disabled ? 0 : springTranslateZ,
      }}
    >
      {/* Label */}
      {label && (
        <motion.label
          style={{
            display: 'block',
            fontSize: currentSize.labelFontSize,
            color: currentVariant.color,
            marginBottom: '8px',
            fontWeight: 500,
            letterSpacing: '0.5px',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
          }}
          animate={{
            y: isFocused || isFilled ? -2 : 0,
            color: isFocused ? currentVariant.focusBorderColor : currentVariant.color
          }}
          transition={{ duration: 0.2 }}
        >
          {label}
          {required && <span style={{ color: 'rgba(220, 53, 69, 0.8)' }}> *</span>}
        </motion.label>
      )}

      {/* Fullname type: two inputs side by side */}
      {isFullname ? (
        <div style={{ display: 'flex', gap: 12 }}>
          <motion.input
            type="text"
            value={value?.firstname || ''}
            onChange={e => handleFullnameChange('firstname', e.target.value)}
            placeholder={placeholder?.firstname || 'First name'}
            disabled={disabled}
            required={required}
            style={{
              flex: 1,
              padding: currentSize.padding,
              fontSize: currentSize.fontSize,
              borderRadius: currentSize.borderRadius,
              background: currentVariant.background,
              color: currentVariant.color,
              border: `1px solid ${getBorderColor()}`,
              outline: 'none',
              fontWeight: 400,
              letterSpacing: '0.3px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              marginBottom: 0
            }}
          />
          <motion.input
            type="text"
            value={value?.lastname || ''}
            onChange={e => handleFullnameChange('lastname', e.target.value)}
            placeholder={placeholder?.lastname || 'Last name'}
            disabled={disabled}
            required={required}
            style={{
              flex: 1,
              padding: currentSize.padding,
              fontSize: currentSize.fontSize,
              borderRadius: currentSize.borderRadius,
              background: currentVariant.background,
              color: currentVariant.color,
              border: `1px solid ${getBorderColor()}`,
              outline: 'none',
              fontWeight: 400,
              letterSpacing: '0.3px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              marginBottom: 0
            }}
          />
        </div>
      ) : (
        // Input wrapper
        <motion.div
          style={{
            position: 'relative',
            borderRadius: currentSize.borderRadius,
            background: currentVariant.background,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            backgroundBlendMode: 'overlay',
            border: `1px solid ${getBorderColor()}`,
            boxShadow: isFocused ? `
              0 10px 30px rgba(0, 0, 0, 0.3),
              0 0 15px ${getShadowColor()},
              inset 0 1px 0 rgba(255, 255, 255, 0.1)
            ` : `
              0 4px 16px rgba(0, 0, 0, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.05)
            `,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transformStyle: 'preserve-3d',
            perspective: '1000px'
          }}
          whileHover={{
            scale: disabled ? 1 : 1.01,
            boxShadow: disabled ? undefined : `
              0 8px 24px rgba(0, 0, 0, 0.2),
              0 0 12px ${getShadowColor()},
              inset 0 1px 0 rgba(255, 255, 255, 0.15)
            `,
            transition: { duration: 0.2 }
          }}
          animate={{
            scale: isFocused ? 1.02 : 1,
            boxShadow: isFocused ? `
              0 15px 40px rgba(0, 0, 0, 0.4),
              0 0 20px ${getShadowColor()},
              inset 0 1px 0 rgba(255, 255, 255, 0.2)
            ` : undefined
          }}
          transition={{ duration: 0.3 }}
        >
          {/* Glass overlay effect */}
          <motion.div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                linear-gradient(
                  135deg,
                  rgba(255, 255, 255, 0.08) 0%,
                  transparent 50%,
                  rgba(255, 255, 255, 0.03) 100%
                )
              `,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              mixBlendMode: 'overlay'
            }}
            animate={{
              opacity: isFocused ? 0.8 : 0.4
            }}
            transition={{ duration: 0.2 }}
          />
          
          {/* Inner glow effect */}
          <motion.div
            style={{
              position: 'absolute',
              top: '2px',
              left: '2px',
              right: '2px',
              bottom: '2px',
              background: `
                radial-gradient(
                  circle at 50% 0%,
                  rgba(255, 255, 255, 0.08) 0%,
                  transparent 70%
                )
              `,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              filter: 'blur(1px)'
            }}
            animate={{
              opacity: isFocused ? 0.4 : 0.2
            }}
            transition={{ duration: 0.2 }}
          />

          {/* Input field */}
          <motion.input
            ref={ref}
            type={type}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            required={required}
            style={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              padding: currentSize.padding,
              fontSize: currentSize.fontSize,
              minHeight: currentSize.minHeight,
              color: currentVariant.color,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              borderRadius: 'inherit',
              fontWeight: 400,
              letterSpacing: '0.3px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              cursor: disabled ? 'not-allowed' : 'text',
              opacity: disabled ? 0.6 : 1,
              // Add padding right for custom buttons
              paddingRight: isNumber ? '48px' : currentSize.padding,
            }}
            animate={{
              y: isFocused ? 1 : 0
            }}
            transition={{ duration: 0.1 }}
            {...props}
          />

          {/* Custom number input buttons */}
          {isNumber && !disabled && (
            <div className="custom-number-arrows" style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              zIndex: 2
            }}>
              <button
                type="button"
                tabIndex={-1}
                aria-label="Increment"
                onClick={() => handleStep(1)}
                style={{
                  width: 28,
                  height: 24,
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 18,
                  cursor: 'pointer',
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s',
                  outline: 'none',
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  padding: 0
                }}
              >
                ▲
              </button>
              <button
                type="button"
                tabIndex={-1}
                aria-label="Decrement"
                onClick={() => handleStep(-1)}
                style={{
                  width: 28,
                  height: 24,
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 18,
                  cursor: 'pointer',
                  marginTop: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s',
                  outline: 'none',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  padding: 0
                }}
              >
                ▼
              </button>
            </div>
          )}

          {/* Placeholder styling */}
          <style>{`
            .liquid-glass-input-container input::placeholder {
              color: ${currentVariant.placeholderColor};
              opacity: 1;
              transition: color 0.3s ease;
            }
            
            .liquid-glass-input-container input:focus::placeholder {
              color: ${currentVariant.placeholderColor}80;
            }
            
            .liquid-glass-input-container input:disabled::placeholder {
              color: ${currentVariant.placeholderColor}40;
            }
            /* Hide default number input arrows for Webkit */
            .liquid-glass-input-container input[type='number']::-webkit-inner-spin-button,
            .liquid-glass-input-container input[type='number']::-webkit-outer-spin-button {
              -webkit-appearance: none;
              margin: 0;
              display: none;
            }
            /* Hide default number input arrows for Firefox */
            .liquid-glass-input-container input[type='number'] {
              -moz-appearance: textfield;
            }
            /* Show custom arrows only on hover/focus of wrapper */
            .liquid-glass-input-container .custom-number-arrows {
              opacity: 0;
              pointer-events: none;
              transition: opacity 0.2s;
            }
            .liquid-glass-input-container:hover .custom-number-arrows,
            .liquid-glass-input-container:focus-within .custom-number-arrows {
              opacity: 1;
              pointer-events: auto;
            }
          `}</style>

          {/* Status indicators */}
          {error && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'rgba(220, 53, 69, 0.8)',
                boxShadow: '0 0 8px rgba(220, 53, 69, 0.5)'
              }}
            />
          )}

          {success && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'rgba(40, 167, 69, 0.8)',
                boxShadow: '0 0 8px rgba(40, 167, 69, 0.5)'
              }}
            />
          )}

          {/* Focus ring */}
          {isFocused && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{
                position: 'absolute',
                top: '-2px',
                left: '-2px',
                right: '-2px',
                bottom: '-2px',
                borderRadius: 'inherit',
                background: `linear-gradient(45deg, ${getShadowColor()}, transparent)`,
                zIndex: -1,
                filter: 'blur(4px)'
              }}
              transition={{ duration: 0.3 }}
            />
          )}
        </motion.div>
      )}

      {/* Helper text */}
      {helperText && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            margin: '8px 0 0 0',
            fontSize: '12px',
            color: error ? 'rgba(220, 53, 69, 0.8)' : 
                   success ? 'rgba(40, 167, 69, 0.8)' : 
                   'rgba(255, 255, 255, 0.6)',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            fontWeight: 400
          }}
        >
          {helperText}
        </motion.p>
      )}
    </motion.div>
  );
});

LiquidGlassInput.displayName = 'LiquidGlassInput';

export default LiquidGlassInput; 